import { Divider, Grid2, keyframes } from '@mui/material';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { AppContext } from '../../../../context/AppContextProvider';
import {
  useConfigDvrAlarmEntriesAsync,
  useGetDvrAlarmEntriesAsync,
  useGetDvrChannelsAsync,
} from '../../../../services/Devices/DvrService/hooks/useMutation';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { DvrAlarmEntriesConfigFormSchema } from '../../../../services/Devices/DvrService/schemas';
import zod from 'zod';
import {
  IDvrAlarmEntryEditionProps,
  IDvrAlarmEntryProps,
  IDvrChannelEditionProps,
  IDvrChannelProps,
} from '../../../../services/Devices/DvrService/types';
import { Add, Check, Close, Remove } from '@mui/icons-material';
import ButtonComponent from '../../../../components/ButtonComponent';
import TextFieldComponent from 'components/TextFieldComponent';
import DropSelectComponent, {
  IDropSelectOptions,
} from '../../../../components/DropSelectComponent';

interface IDvrAlarmEntriesConfigTabProps {
  selectedDevice: any;
}

type DvrAlarmEntriesConfigFormInput = zod.infer<
  typeof DvrAlarmEntriesConfigFormSchema
>;

const DvrAlarmEntriesConfigTab = ({
  selectedDevice,
}: IDvrAlarmEntriesConfigTabProps) => {
  const { showSnackbar, setIsShowLoadingComponent } = useContext(AppContext);
  const { control, reset, handleSubmit } =
    useForm<DvrAlarmEntriesConfigFormInput>({
      resolver: zodResolver(DvrAlarmEntriesConfigFormSchema),
      defaultValues: {
        alarmEntries: [],
      },
    });
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'alarmEntries',
    keyName: 'key',
  });
  const [dvrAlarmEntries, setDrvAlarmEntries] = useState<IDvrAlarmEntryProps[]>(
    [],
  );
  const [channelOptions, setChannelOptions] = useState<IDvrChannelProps[]>([]);
  const [editionSnapshot, setEditionSnapshot] = useState<
    IDvrChannelEditionProps[]
  >([]);
  const [key, setKey] = useState<number>(0);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const { mutateAsync: getChannels } = useGetDvrChannelsAsync();
  const { mutateAsync: getAlarmEntries } = useGetDvrAlarmEntriesAsync();
  const { mutateAsync: configAlarmEntries } = useConfigDvrAlarmEntriesAsync();

  const getChannelsSelectionOptions = (): IDropSelectOptions[] => {
    return channelOptions.map((i) => {
      const channel = i.channel + 1;
      return {
        option: channel.toString(),
        value: i.id,
      } as IDropSelectOptions;
    });
  };

  const renderItemByAlarmEntry = (alarmEntry: IDvrAlarmEntryEditionProps) => {
    const channels = dvrAlarmEntries.flatMap((i) =>
      i.channels.filter((j) => alarmEntry.channelIds?.includes(j.id)),
    );
    const entryNum = alarmEntry.entry !== undefined ? alarmEntry.entry + 1 : 0;

    if (!channels || channels.length === 0) {
      return (
        <Grid2 container flexDirection="column" gap={1}>
          <Grid2>
            <span>Entrada de Alarme: {entryNum}</span>
          </Grid2>

          <Grid2>
            <span>Canais: Nenhum canal vinculado</span>
          </Grid2>
        </Grid2>
      );
    }

    return (
      <Grid2 container flexDirection="column" gap={1}>
        <Grid2 container alignItems="center" gap={1}>
          <Grid2>
            <span>Entrada de Alarme: {entryNum}</span>
          </Grid2>
        </Grid2>

        <Grid2 container alignItems="center" gap={1} flexDirection="row">
          <Grid2>
            <span>Canais: </span>
          </Grid2>
          <Grid2 container direction="row" alignItems="center" gap={1}>
            {channels.map((i, idx) => {
              const channel = i.channel != undefined ? i.channel + 1 : 0;
              return (
                <Grid2
                  key={idx}
                  px={1}
                  py={0.5}
                  borderRadius={2}
                  border={'1px solid'}
                  borderColor={'gray'}
                >
                  {channel}
                </Grid2>
              );
            })}
          </Grid2>
        </Grid2>
      </Grid2>
    );
  };

  const renderEditionItemByAlarmEntry = (idx: number) => {
    return (
      <Grid2 container alignItems={'center'} gap={1}>
        <Grid2>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                key={key}
                label="Entrada de Alarme"
                onChangeText={(val) => {
                  const num = Number(val) - 1;
                  onChange(num);
                }}
                type={'number'}
                value={value + 1}
                sx={{ marginBottom: -2 }}
                size={'small'}
                errorMessage={error?.message}
              />
            )}
            name={`alarmEntries.${idx}.entry`}
            control={control}
          />
        </Grid2>
        <Grid2>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <DropSelectComponent
                key={key}
                label={'Canais'}
                valueSelected={value}
                onChange={onChange}
                options={getChannelsSelectionOptions()}
                isMultiple={true}
                size={'small'}
                isRequired={false}
                isError={!!error}
                fullwidth={true}
                errorMessage={error?.message}
              />
            )}
            name={`alarmEntries.${idx}.channelIds`}
            control={control}
          />
        </Grid2>
        <Grid2 sx={{ flexGrow: 1 }} />
        <Grid2>
          <ButtonComponent
            variant={'outlined'}
            color={'error'}
            size={'small'}
            onClick={() => removeAlarmEntryItem(idx)}
          >
            <Remove sx={{ mr: '4px', width: '20px', height: '20px' }} />
            <span>Remover</span>
          </ButtonComponent>
        </Grid2>
      </Grid2>
    );
  };

  const renderEditionButtons = () => {
    if (isEditing) {
      return (
        <Grid2 container alignItems={'center'} gap={2}>
          <Grid2>
            <ButtonComponent
              variant={'outlined'}
              color={'success'}
              size={'medium'}
              onClick={addAlarmEntryItem}
            >
              <Add sx={{ mr: '4px', width: '20px', height: '20px' }} />
              <span>Adicionar</span>
            </ButtonComponent>
          </Grid2>
          <Grid2 sx={{ flexGrow: 1 }} />
          <Grid2>
            <ButtonComponent
              color={'primary'}
              size={'medium'}
              onClick={handleSubmit(saveEdition)}
            >
              <Check sx={{ mr: '4px', width: '20px', height: '20px' }} />
              <span>Salvar</span>
            </ButtonComponent>
          </Grid2>
          <Grid2>
            <ButtonComponent
              color={'error'}
              size={'medium'}
              onClick={cancelEditionMode}
            >
              <Close sx={{ mr: '4px', width: '20px', height: '20px' }} />
              <span>Cancelar</span>
            </ButtonComponent>
          </Grid2>
        </Grid2>
      );
    }

    return (
      <Grid2 container alignItems={'center'}>
        <ButtonComponent
          color={'secondary'}
          size={'medium'}
          onClick={startEditionMode}
        >
          Editar
        </ButtonComponent>
      </Grid2>
    );
  };

  const addAlarmEntryItem = () => {
    if (fields.length === 0) {
      append({ entry: 0, channelIds: [] });
    } else {
      const lastItem = fields[fields.length - 1].entry;
      append({ entry: lastItem + 1, channelIds: [] });
    }
  };

  const removeAlarmEntryItem = (idx: number) => {
    remove(idx);
  };

  const startEditionMode = () => {
    setEditionSnapshot(fields);
    setIsEditing(true);
  };

  const endEditionMode = async () => {
    await fetchAlarmEntries();
    setEditionSnapshot([]);
    setIsEditing(false);
  };

  const cancelEditionMode = () => {
    setIsEditing(false);
    if (editionSnapshot && editionSnapshot.length > 0) {
      reset({ alarmEntries: editionSnapshot }, { keepDefaultValues: true });
    }
    setEditionSnapshot([]);
  };

  const saveEdition = async (data: DvrAlarmEntriesConfigFormInput) => {
    setIsShowLoadingComponent(true);
    await configAlarmEntries({ id: selectedDevice.id, data })
      .then(() => {
        showSnackbar(
          'Configuração de entradas de alarme efetuada com sucesso!',
          'success',
        );
        endEditionMode();
      })
      .catch((error) => {
        cancelEditionMode();
      })
      .finally(() => setIsShowLoadingComponent(false));
  };

  const fetchChannelOptions = useCallback(async () => {
    if (!channelOptions || channelOptions?.length === 0) {
      await getChannels(selectedDevice.id).then(setChannelOptions);
    }
  }, [channelOptions, selectedDevice]);

  const fetchAlarmEntries = useCallback(async () => {
    await getAlarmEntries(selectedDevice.id).then((response) => {
      setDrvAlarmEntries(response);

      const alarmEntriesData: IDvrAlarmEntryEditionProps[] = response.map(
        (i) => ({
          id: i.id,
          entry: i.entry,
          channelIds: i.channels.map((j) => j.id),
        }),
      );

      reset({ alarmEntries: alarmEntriesData }, { keepDefaultValues: true });
    });
  }, [getChannels, reset, selectedDevice]);

  useEffect(() => {
    if (selectedDevice && fields.length === 0) {
      fetchAlarmEntries();
      fetchChannelOptions();
    }
  }, [selectedDevice]);

  useEffect(() => {
    setKey(Math.random() * 10);
  }, []);

  return (
    <Grid2
      container
      direction="column"
      size={{ md: 12, lg: 8 }}
      p={1}
      spacing={1}
    >
      <Grid2>
        <>{renderEditionButtons()}</>
      </Grid2>
      <Grid2>
        <Divider
          orientation="horizontal"
          sx={{ backgroundColor: 'gray', mb: 1 }}
        />
      </Grid2>
      <Grid2 sx={{ maxHeight: 300, overflowY: 'auto' }}>
        {fields ? (
          fields.map((f, idx) => (
            <Grid2
              key={f.key}
              size={12}
              mb={2}
              p={isEditing ? 1 : 2}
              borderRadius={2}
              border={'1px solid'}
              borderColor={'gray'}
            >
              {isEditing
                ? renderEditionItemByAlarmEntry(idx)
                : renderItemByAlarmEntry(f)}
            </Grid2>
          ))
        ) : (
          <Grid2>Nenhuma entrada de alarme registrada</Grid2>
        )}
      </Grid2>
    </Grid2>
  );
};

export default DvrAlarmEntriesConfigTab;
